// Tipurile Pasului / zonei «categorii» — desprinse din `tipuri.ts`; orice importator
// le ia tot din `./tipuri` (bariera re-exportă fiecare fișier de aici).

// --- catalogul CATEGORIILOR de verificări (tooltip-ul Pasului 19) ---------
// Explicațiile trăiesc în `catalog_verificari.py`, nu aici: același text
// merge și în rapoarte, și în diagnosticul 4.H, și în contextul LLM.

export interface SolutieCategorie {
  ce: string;
  efect: string;
  cost: string;
  automat?: string | null;
  sursa?: string | null;
}

export interface SimbolVerificare {
  simbol: string;
  nume: string;
  de_unde: string;
}

export interface DivergentaCategorie {
  fata_de: string;
  implementat: string;
  normativ: string;
  efect: string;
}

export interface CategorieVerificare {
  eticheta: string;
  /** O PROPOZIȚIE: ce se compară cu ce. E singurul text arătat fără hover —
   *  restul explicației (`ce`, `formula`, `simboluri`, `solutii`) se citește
   * la cerere. Lipsește pe un server mai vechi decât. */
  scurt?: string;
  pas: number;
  pas_nume: string;
  ce: string;
  formula: string;
  /** de unde vine formula: "foaie" | "ro" | "ec" — prioritatea casei */
  sursa_formula?: string;
  sursa_formula_nume?: string;
  articol: string;
  simboluri: SimbolVerificare[];
  cand: string;
  divergenta?: DivergentaCategorie;
  solutii: SolutieCategorie[];
  /** cheia tematicii de care aparține categoria (v. `TematicaVerificari`) */
  tematica?: string | null;
  /** CRITICAL = driftul și biciul, deplasările cerute trecute fără excepție;
   *  HIGH = restul globalelor (o picată aici schimbă eforturile din tot ce
   *  urmează, deci verdictele de dedesubt sunt pe cifre provizorii); LOW =
   *  reparabilă local; MED = restul. Din `catalog_verificari.prioritate_tip`. */
  prioritate?: "CRITICAL" | "HIGH" | "MED" | "LOW";
  /** o picată din categoria asta oprește livrarea, sau doar se raportează?
   *  Implicit `true`; `false` numai pentru categoriile din
   *  `catalog_verificari.TIPURI_NEBLOCANTE` (separarea
   *  perioadelor nu e cerință de normativ, iar structuristul a admis să nu
   *  treacă). Lipsește pe un server mai vechi. */
  blocheaza?: boolean;
  /** validitatea analizei: se verifică intern, nu se afișează; picată ⇒
   * eroare de lanț */
  interna?: boolean;
  /** verificarea trece când valoarea e MAI MARE decât pragul (separarea
   *  perioadelor, masa participantă, răsturnarea, cutia rigidă): rezerva se
   *  citește valoare/prag, nu prag/valoare */
  inversat?: boolean;
}

/** o regulă de traducere «numele emis de pas» → categoria din catalog */
export interface RegulaTip {
  pas: string;
  cum: "contine" | "incepe" | "grupa" | "id";
  text: string;
  tip: string;
}

/** O TEMATICĂ: nivelul de deasupra categoriei (`catalog_verificari.TEMATICI`).
 *  `tipuri` enumeră categoriile ei — un element terminat cu „_" e PREFIX. */
export interface TematicaVerificari {
  cheie: string;
  eticheta: string;
  ce: string;
  tipuri: string[];
}

export interface CatalogVerificari {
  categorii: Record<string, CategorieVerificare>;
  /** tematicile, în ordinea de afișare (lipsesc pe un server mai vechi) */
  tematici?: TematicaVerificari[];
  pasi: Record<string, string>;
  surse: Record<string, string>;
  prefixe: Record<string, string>;
  reguli_tip: RegulaTip[];
  /** ce înseamnă HIGH/MED/LOW, în cuvintele serverului (lipsește pe un
   *  server mai vechi) */
  prioritati?: { cod: string; nume: string; ce: string }[];
  /** categoriile RAPORTATE care nu blochează verdictul */
  neblocante?: string[];
  /** tipurile dintr-o familie (`prefixe`) cu treapta lor: explicația e a
   *  familiei, treapta, blocarea și interna sunt ale tipului
   *  (`catalog_verificari._subtipurile_numite`; lipsește pe un server mai
   *  vechi) */
  subtipuri?: Record<
    string,
    Pick<CategorieVerificare, "prioritate" | "blocheaza" | "interna">
  >;
  prioritate_surse: string[];
}

/** O MĂRIME din catalogul de pe server (`catalog_marimi.py`): p_conv, p_ef,
 *  K_s, B, D_f … — lucrurile care au o VALOARE, spre deosebire de categoriile
 *  de verificări, care trec sau pică. */
export interface MarimeCatalog {
  simbol: string;
  eticheta: string;
  unitate?: string;
  ce: string;
  de_ce?: string;
  formula?: string;
  articol?: string;
  parametri?: { simbol: string; nume: string; sursa: string }[];
  /** VALOARE aplicată / LIMITĂ / CAPACITATE / GEOMETRIE — se arată pe card,
   *  fără hover: cea mai frecventă confuzie e „e cifra asta cât apasă sau cât
   * duce?" */
  natura?: "valoare" | "limita" | "capacitate" | "geometrie" | "derivat"
    | "intrare";
  /** cum s-a obținut CONCRET valoarea, nu la ce folosește mărimea */
  cum_obtinut?: string;
  /** pentru simbolurile din registru (`simboluri.py`): ce înseamnă în
   *  fiecare verificare care le declară */
  contexte?: { categorie: string; eticheta: string; nume: string; de_unde: string }[];
}

export interface CatalogMarimi {
  marimi: Record<string, MarimeCatalog>;
  nr: number;
  /** `simbol -> cheie`, emis de `catalog_marimi.simboluri()`: cu el se
   *  adnotează AUTOMAT simbolurile din texte, formule și capete de tabel
   *  (v. `<TextSimboluri>`). Nu se construiește în interfață — ar rămâne în
   *  urma catalogului la prima mărime nouă. */
  simboluri?: Record<string, string>;
}

/** Un PAS din catalogul de pe server (`catalog_pasi.py`): «ce face pasul
 *  acesta», arătat la hover pe titlul pasului și în starea «n-a rulat încă».
 *  Textul e unul singur pentru amândouă. */
export interface PasCatalog {
  pas: number;
  nume: string;
  intrebarea: string;
  ce_face: string;
  primeste: { ce: string; de_unde: string }[];
  decide: string[];
  verifica: string[];
  da_mai_departe: { ce: string; cui: string }[];
  capcane?: string[];
}

export interface CatalogPasi {
  pasi: Record<string, PasCatalog>;
}

/** Categoria unei verificări, din pasul care a emis-o și numele ei — aceleași
 *  reguli ca `catalog_verificari.tip_din_nume` (vin de la server, nu sunt
 *  rescrise aici). `grupa` (Pașii 13/14) și `id` (Pasul 10) au prioritate. */
export function tipDinNume(
  catalog: CatalogVerificari | null,
  pas?: string,
  nume?: string,
  grupa?: string,
  id?: string
): string | null {
  if (!catalog || !pas) return null;
  const n = nume ?? "";
  for (const r of catalog.reguli_tip ?? []) {
    if (r.pas !== pas) continue;
    if (r.cum === "grupa") {
      if (grupa) return `${r.tip}${grupa}`;
      continue;
    }
    if (r.cum === "id") {
      if (id) return id;
      continue;
    }
    if (r.cum === "contine" && n.includes(r.text)) return r.tip;
    if (r.cum === "incepe" && n.startsWith(r.text)) return r.tip;
  }
  return null;
}

/** Explicația unei categorii, cu prefixele rezolvate (amplasare_* → amplasare).
 *  Tipul din familie care are treapta lui (`subtipuri`) ține explicația
 *  familiei, dar treapta, blocarea și interna lui. */
export function categoriaDin(
  catalog: CatalogVerificari | null,
  tip: string
): CategorieVerificare | null {
  if (!catalog) return null;
  if (catalog.categorii[tip]) return catalog.categorii[tip];
  for (const [prefix, cheie] of Object.entries(catalog.prefixe ?? {})) {
    if (!tip.startsWith(prefix)) continue;
    const familia = catalog.categorii[cheie];
    if (!familia) return null;
    const propriu = catalog.subtipuri?.[tip];
    return propriu ? { ...familia, ...propriu } : familia;
  }
  return null;
}

